import React from 'react';
import { Link } from 'react-router-dom';
import './SpecialtyCare.css';

function SpecialtyCare() {
  return (
    <div className="specialty-care-page">
      {/* Header/Navigation */}
      <header className="header">
        <nav className="navbar">
          <div className="logo">
            <Link to="/"><h1>Malach <span>Healthcare</span></h1></Link>
          </div>
          <ul className="nav-links">
            <li><Link to="/">Home</Link></li>
            <li><Link to="/#services">Services</Link></li>
            <li><Link to="/#contact">Contact</Link></li>
          </ul>
          <Link to="/#contact" className="btn btn-primary">Book Consultation</Link>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="service-hero specialty-hero">
        <div className="service-hero-content">
          <h1>Specialty Care When It Matters Most</h1>
          <p>Honoring the whole person—their experiences, challenges, independence, and dignity</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="service-content">
        <div className="service-intro">
          <p className="intro-highlight">
            Caring for a loved one with complex needs is a journey filled with devotion, strength, 
            and—at times—overwhelming uncertainty. Families and veterans alike deserve care that 
            not only meets medical needs, but also honors the whole person: their experiences, 
            challenges, independence, and dignity.
          </p>
          <p className="belief-statement">
            At Malach Healthcare, we believe specialty care should feel human, compassionate, 
            and deeply respectful.
          </p>
        </div>

        <div className="unique-needs">
          <h2>Understanding Unique Needs</h2>
          <p>
            No two individuals are the same. People living with disabilities and veterans often 
            face physical, emotional, and behavioral challenges that require more than standard care. 
            These needs may be lifelong, service-related, or developed over time—but they all deserve 
            informed, patient-centered support.
          </p>
          <div className="needs-grid">
            <div className="need-item">
              <div className="need-icon">🎓</div>
              <h3>Trained Providers</h3>
              <p>Providers trained to understand complex and long-term conditions</p>
            </div>
            <div className="need-item">
              <div className="need-icon">📋</div>
              <h3>Individualized Plans</h3>
              <p>Individualized care plans that adapt as needs change</p>
            </div>
            <div className="need-item">
              <div className="need-icon">🏠</div>
              <h3>Safe Environment</h3>
              <p>A calm, structured environment that promotes safety and comfort</p>
            </div>
            <div className="need-item">
              <div className="need-icon">✨</div>
              <h3>Respect & Identity</h3>
              <p>Respect for personal history, independence, and identity</p>
            </div>
          </div>
        </div>

        <div className="veterans-section">
          <div className="veterans-content">
            <div className="veterans-icon">🎖️</div>
            <h2>Honoring Our Veterans</h2>
            <p>
              Veterans carry experiences that deserve recognition and understanding. Whether navigating 
              service-related injuries, chronic conditions, or emotional stress, veterans benefit from 
              care teams who acknowledge their service and understand the lasting impact it can have 
              on health and daily life.
            </p>
            <p className="veterans-promise">
              At Malach Healthcare, veterans are treated with the respect, patience, and specialized 
              attention they have earned—never as a diagnosis, but as individuals with stories, 
              strengths, and goals.
            </p>
          </div>
        </div>

        <div className="family-support">
          <h2>Supporting Families and Caregivers</h2>
          <p className="family-intro">
            Families and caregivers are not just involved—they are essential. We know how heavy 
            the responsibility can feel, and how hard it is to find care you can truly trust.
          </p>
          <div className="approach-list">
            <h3>Our Approach Emphasizes:</h3>
            <ul>
              <li>
                <span className="check-icon">✓</span>
                Clear communication and transparency
              </li>
              <li>
                <span className="check-icon">✓</span>
                Collaboration with families and support networks
              </li>
              <li>
                <span className="check-icon">✓</span>
                Compassionate professionals who listen and respond
              </li>
              <li>
                <span className="check-icon">✓</span>
                Peace of mind knowing your loved one is understood and valued
              </li>
            </ul>
          </div>
        </div>

        <div className="difference-section">
          <h2>Why Specialty Care Makes a Difference</h2>
          <p>
            Choosing specialty care means choosing expertise, empathy, and intentional support. 
            It means knowing your loved one is in an environment designed for their specific 
            needs—where care is proactive, not reactive, and where quality of life is a priority.
          </p>
        </div>

        <div className="mission-block">
          <h3>Our Mission</h3>
          <p>
            To provide specialized care that restores confidence, preserves dignity, and supports 
            both individuals and the families who stand beside them.
          </p>
        </div>

        <div className="closing-message">
          <p className="highlight">
            You do not have to walk this journey alone.
          </p>
          <p>
            With the right care, understanding, and support, better days are possible—for your 
            loved one, and for you.
          </p>
          <Link to="/#contact" className="btn btn-primary btn-large">Book A Free Consultation</Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-section">
            <h3>Malach <span>Healthcare</span></h3>
            <p>Empowering golden years with compassionate care.</p>
            <div className="social-links">
              <a href="#facebook">📘</a>
              <a href="#twitter">🐦</a>
              <a href="#instagram">📷</a>
              <a href="#linkedin">💼</a>
            </div>
          </div>
          <div className="footer-section">
            <h4>Quick Links</h4>
            <ul>
              <li><Link to="/">Home</Link></li>
                <li><Link to="/#services">Services</Link></li>
              <li><Link to="/#contact">Contact</Link></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Our Services</h4>
            <ul>
              <li><Link to="/personal-care">Personal Care</Link></li>
              <li><Link to="/companion-care">Companion Care</Link></li>
              <li><Link to="/dementia-care">Dementia Care</Link></li>
              <li><Link to="/specialty-care">Specialty Care</Link></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Contact Info</h4>
            <p>📍 740 Main St<br />Waltham, MA 02451</p>
            <p>📞 (781) 697-6330</p>
            <p>✉️ info@malachhealthcare.com</p>
          </div>
        </div>
        <div className="footer-bottom">
          <p>Copyright © 2025 | All Rights Reserved | Malach Healthcare</p>
        </div>
      </footer>
    </div>
  );
}

export default SpecialtyCare;
